import { mdiCommentQuoteOutline } from '@mdi/js';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { alpha, AppBar, Box, Button, Divider, Drawer, IconButton, Portal, Stack, SvgIcon, Toolbar, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { trackAnalytics } from '@puffin/app-frame-api';
import type { Theme } from '@mui/material';
import { useState } from 'react';
import { AppBrand } from './AppBrand';
import { AppNavigation } from './AppNavigation';
import { UserSummary } from './UserSummary';
import { DevToolsButton } from './DevToolsButton';

const drawerWidth = 280;
const collapsedDrawerWidth = 80;

const glassSurface = (theme: Theme) => ({
  background: `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0.12)} 78%, ${alpha(theme.palette.primary.main, 0.035)} 100%)`,
  backdropFilter: 'blur(3px) saturate(135%)',
  WebkitBackdropFilter: 'blur(3px) saturate(135%)',
});

export const NavigationDrawer = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const desktopWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <>
      {!isDesktop && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={(theme) => ({
            display: { md: 'none' },
            borderBottom: 1,
            ...glassSurface(theme),
          })}
        >
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => {
                trackAnalytics('MOBILE_NAVIGATION_OPENED', {});
                setMobileOpen(true);
              }}
              aria-label="Open navigation"
              sx={{ mr: 1.5 }}
            >
              <MenuRoundedIcon />
            </IconButton>
            <AppBrand />
          </Toolbar>
        </AppBar>
      )}

      {isDesktop && (
        <>
          <Box aria-hidden="true" sx={{ width: desktopWidth, flexShrink: 0 }} />
          <Portal>
            <Drawer
              variant="permanent"
              sx={(theme) => ({
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': {
                  ...glassSurface(theme),
                  width: desktopWidth,
                  boxSizing: 'border-box',
                  borderRight: 1,
                  borderRightColor: alpha(theme.palette.background.paper, 0.7),
                  boxShadow: 8,
                },
              })}
            >
              <DrawerContent
                collapsed={collapsed}
                onToggleCollapsed={() => {
                  trackAnalytics('DESKTOP_NAVIGATION_TOGGLED', { collapsed: !collapsed });
                  setCollapsed((current) => !current);
                }}
              />
            </Drawer>
          </Portal>
        </>
      )}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => {
            trackAnalytics('MOBILE_NAVIGATION_CLOSED', {});
            setMobileOpen(false);
          }}
          ModalProps={{ keepMounted: true }}
          sx={(theme) => ({
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth, ...glassSurface(theme) },
          })}
        >
          <DrawerContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      )}
    </>
  );
};

const DrawerContent = ({
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) => (
  <Stack sx={{ height: '100%' }}>
    <Stack direction={collapsed ? 'column' : 'row'} spacing={1} sx={{ px: collapsed ? 1 : 2.5, py: 2, alignItems: 'center' }}>
      <Box sx={{ flex: collapsed ? undefined : 1, minWidth: 0 }}>
        <AppBrand compact={collapsed} />
      </Box>
      {onToggleCollapsed && (
        <Tooltip title={collapsed ? 'Expand navigation' : 'Collapse navigation'} placement="right">
          <IconButton aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'} aria-expanded={!collapsed} onClick={onToggleCollapsed}>
            {collapsed ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
    <Divider />
    <Box sx={{ overflowY: 'auto', flex: 1 }}>
      <AppNavigation onNavigate={onNavigate} collapsed={collapsed} />
    </Box>
    <Stack spacing={1} sx={{ px: collapsed ? 1 : 2.5, pt: 1.5, alignItems: 'center' }}>
      <DevToolsButton collapsed={collapsed} onNavigate={onNavigate} />
      {collapsed ? (
        <Tooltip title="Give Feedback" placement="right">
          <IconButton
            component="a"
            href="https://github.com/itdesign-gmbh/mp-platformpuffin/issues/new/choose"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Give Feedback"
            onClick={() => trackAnalytics('FEEDBACK_OPENED', {})}
          >
            <SvgIcon>
              <path d={mdiCommentQuoteOutline} />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          component="a"
          href="https://github.com/itdesign-gmbh/mp-platformpuffin/issues/new/choose"
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          startIcon={
            <SvgIcon>
              <path d={mdiCommentQuoteOutline} />
            </SvgIcon>
          }
          fullWidth
          onClick={() => trackAnalytics('FEEDBACK_OPENED', {})}
        >
          Give Feedback
        </Button>
      )}
    </Stack>
    <UserSummary collapsed={collapsed} />
  </Stack>
);
